import Dexie from "dexie";

/**
 * Local IndexedDB database for offline-first operation.
 *
 * Tables:
 *  syncQueue     – pending / failed write operations waiting to be sent to the server
 *  students      – cached student lists keyed by [schoolId+userId+termId+classroomId]
 *  assessments   – cached assessments keyed by [schoolId+userId+termId+classroomId]
 *  records       – cached score records keyed by [schoolId+userId+termId+classroomId+subjectId]
 *  comments      – cached comments (all types) keyed by composite key
 *  traitGrades   – cached behavior/trait grades for a student in a classroom
 *  traits        – cached trait + behavior definitions
 *  grades        – cached grade band definitions
 *  quizSessions  – in-progress quiz answer state for offline quiz completion
 *  preloadStatus – per-classroom preload metadata (status, staleness, counts)
 */
const db = new Dexie("schoolaOfflineDB");

db.version(1).stores({
  // ---- sync queue -----------------------------------------------------------
  syncQueue:
    "++id, entityType, action, status, classroomId, subjectId, studentId, quizId, createdAt, retries",

  // ---- read caches (v1 primary keys – replaced in v4) ----------------------
  students: "classroomId, cachedAt",
  assessments: "classroomId, cachedAt",
  records: "[subjectId+classroomId+termId], cachedAt",
  comments: "++id, classroomId, subjectId, sectionId, departmentId, termId, type, cachedAt",
  traitGrades: "[studentId+classroomId+termId], cachedAt",
  traits: "subsectionId, cachedAt",
  grades: "++id, type, subsectionId, cachedAt",

  // ---- quiz sessions --------------------------------------------------------
  quizSessions: "[quizId+studentId], status, updatedAt",
});

// Version 2 – adds nav-data and attendance caches
db.version(2).stores({
  myClasses: "id, cachedAt",
  subjects: "id, cachedAt",
  sections: "id, cachedAt",
  departments: "id, cachedAt",
  allClasses: "id, cachedAt",
  attendance: "classroomId, cachedAt",
});

// Version 3 – drop all cache tables whose primary keys need to change.
// Dexie (IndexedDB) does not support changing a primary key in-place; tables
// are dropped here and recreated with namespaced compound keys in v4.
// syncQueue keeps ++id so only its indexes are updated (allowed).
db.version(3).stores({
  students: null,
  assessments: null,
  records: null,
  comments: null,
  traitGrades: null,
  traits: null,
  grades: null,
  attendance: null,
  syncQueue:
    "++id, entityType, action, status, schoolId, userId, sessionId, termId, classroomId, subjectId, studentId, quizId, createdAt, retries",
});

// Version 4 – recreate cache tables with namespaced compound primary keys and
// add preloadStatus tracking table.
db.version(4).stores({
  students:
    "[schoolId+userId+termId+classroomId], schoolId, userId, termId, classroomId, cachedAt",
  assessments:
    "[schoolId+userId+termId+classroomId], schoolId, userId, termId, classroomId, cachedAt",
  records:
    "[schoolId+userId+termId+classroomId+subjectId], schoolId, userId, termId, classroomId, subjectId, cachedAt",
  comments:
    "[schoolId+userId+termId+studentId+classroomId+sectionId+departmentId+type], [schoolId+userId+termId+classroomId+subjectId+type], schoolId, userId, termId, studentId, classroomId, subjectId, sectionId, departmentId, type, cachedAt",
  traitGrades:
    "[schoolId+userId+termId+classroomId+studentId], schoolId, userId, termId, classroomId, studentId, cachedAt",
  traits:
    "[schoolId+userId+subsectionId], schoolId, userId, subsectionId, cachedAt",
  grades:
    "[schoolId+userId+subsectionId+type], schoolId, userId, subsectionId, type, cachedAt",
  attendance:
    "[schoolId+userId+termId+classroomId], schoolId, userId, termId, classroomId, cachedAt",
  preloadStatus:
    "[schoolId+userId+termId+classroomId], schoolId, userId, termId, classroomId, status, stale, lastFetchedAt, lastValidatedAt",
});

// Version 5 – add startedAt index to quizSessions for client-side timer persistence.
// The actual startedAt value comes from the server; this index allows efficient lookups.
db.version(5).stores({
  quizSessions: "[quizId+studentId], status, updatedAt, startedAt",
});

// Version 6 – add classroomTraits table for classroom-specific trait definitions cache.
// The for-classroom endpoint returns either classroom-specific traits or subsection fallback.
db.version(6).stores({
  classroomTraits: "[schoolId+userId+classroomId], schoolId, userId, classroomId, cachedAt",
});

// Safety net: if the browser already has a corrupted database from the prior
// failed v3 migration, delete it and re-open so it is rebuilt at v4.
db.open().catch(async (err) => {
  if (err.name === "UpgradeError" || err.name === "DatabaseClosedError") {
    await Dexie.delete("schoolaOfflineDB");
    await db.open();
  }
});

export default db;
